'use client';

import { Box, Chip, Stack, Tooltip, Typography } from '@/primitives';
import {
  useServiceHealth,
  type OverallStatus,
  type ServiceHealthEntry,
} from '@/hooks/useServiceHealth';

const SERVICE_LABELS: Record<string, string> = {
  registry: 'Registry',
  search: 'Search',
  searchGraphql: 'Search GraphQL',
  auth: 'Auth',
};

function StatusDot({ status }: { status: 'healthy' | 'unhealthy' }) {
  return (
    <Box
      sx={{
        width: 8,
        height: 8,
        borderRadius: '50%',
        bgcolor: status === 'healthy' ? 'success.main' : 'error.main',
        flexShrink: 0,
      }}
    />
  );
}

function formatDeployTime(iso: string | undefined): string {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const day = d.getDate().toString().padStart(2, '0');
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    const year = d.getFullYear();
    const hours = d.getHours().toString().padStart(2, '0');
    const minutes = d.getMinutes().toString().padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  } catch {
    return iso;
  }
}

function TooltipContent({
  services,
}: {
  services: Record<string, ServiceHealthEntry>;
}) {
  const deployTime = formatDeployTime(process.env.NEXT_PUBLIC_BUILD_TIME);

  return (
    <Stack component="span" gap={0.5} sx={{ py: 0.5 }}>
      {deployTime && (
        <Typography
          component="span"
          variant="bodysmall"
          sx={{ mb: 0.5, opacity: 0.8 }}
        >
          Deployed: {deployTime}
        </Typography>
      )}
      {(Object.keys(SERVICE_LABELS) as Array<keyof typeof SERVICE_LABELS>).map(
        (key) => {
          const entry = services[key];
          if (!entry) return null;
          return (
            <Stack key={key} alignItems="center" direction="row" gap={1}>
              <StatusDot status={entry.status} />
              <Typography component="span" variant="bodysmall">
                {SERVICE_LABELS[key]}:{' '}
                {entry.status === 'healthy' ? 'Healthy' : 'Unhealthy'}
                {entry.responseTimeMs > 0 && ` (${entry.responseTimeMs}ms)`}
              </Typography>
            </Stack>
          );
        },
      )}
    </Stack>
  );
}

function VersionChipIcon({ overall }: { overall: OverallStatus }) {
  const statusBg =
    overall === 'healthy'
      ? 'success.main'
      : overall === 'degraded'
        ? 'warning.main'
        : overall === 'unhealthy'
          ? 'error.main'
          : 'grey.500';

  return (
    <Box
      sx={{
        width: 6,
        height: 6,
        borderRadius: '50%',
        bgcolor: statusBg,
        ml: 0.25,
        flexShrink: 0,
      }}
    />
  );
}

export function HealthStatusChip() {
  const { services, overall } = useServiceHealth();

  const versionString = `v${process.env.version ?? ''}.${process.env.NEXT_PUBLIC_PRODUCTION_BUILD_NUMBER ?? ''}.${process.env.NEXT_PUBLIC_STAGING_BUILD_NUMBER ?? ''}.${process.env.NEXT_PUBLIC_DEV_BUILD_NUMBER ?? ''}`;

  return (
    <Tooltip title={<TooltipContent services={services} />}>
      <Chip
        icon={<VersionChipIcon overall={overall} />}
        label={versionString}
        size="small"
        variant="outlined"
        sx={{
          display: { xs: 'none', sm: 'inline-flex' },
          mr: '20px',
          ml: '20px',
          height: '22px',
          pl: 1.25,
          pr: 0.75,
          fontSize: '10px',
          '& .MuiChip-label': {
            fontSize: '10px',
          },
          '& .MuiChip-icon': {
            marginLeft: 0.25,
            marginRight: 0,
            fontSize: 'inherit',
          },
        }}
      />
    </Tooltip>
  );
}
